import React, {
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import credentialsService from 'services/credentialsService';
import api from 'api';
import AuthContext from './Auth.context';
import {
  AuthBody,
  LoginValues,
  RegAuthBody,
  TwoFactorLoginValues,
} from 'models/AuthBody';
import {
  isAuthBody,
  isRegAuthBody,
} from 'router/subrouters/Auth/utils/isAuthBody';
import CurrentUserProvider from 'providers/CurrentUser/CurrentUser.provider';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import FourDotLoader from '../../components/FourDotLoader/FourDotLoader.component';

const Auth: React.FC<{ children: React.ReactNode }> = (props) => {
  const { children } = props;
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { popError } = useContext(PopNotificationsContext);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(
    !!credentialsService.token || !!credentialsService.registrationToken,
  );

  const getToken = useCallback(() => credentialsService.token, []);

  const onLoginReq = useCallback((loginResponse: AuthBody) => {
    const { tokens } = loginResponse;

    if (tokens) {
      credentialsService.saveAuthBody({tokens});
      setIsLoggedIn(true);
    }
  }, []);

  const onRegLoginReq = useCallback((loginResponse: RegAuthBody) => {
    const { registrationToken } = loginResponse;

    if (registrationToken) {
      credentialsService.saveAuthBody({registrationToken});
      setIsLoggedIn(true);
    }
  }, []);

  const loginRequest = useCallback(
    async (values: LoginValues, onSuccess?: (data: AuthBody) => void) => {
      try {
        const loginResponse = await api.auth.login({
          email: values.email,
          password: values.password,
        });
        if (isRegAuthBody(loginResponse.data)) {
          onRegLoginReq(loginResponse.data);
        }
        onSuccess?.(loginResponse.data);
      } catch (err) {
        console.error('Error:', err);
        popError(t('invalid-credentials'));
      }
    },
    [onRegLoginReq, popError, t],
  );

  const twoFactorLoginRequest = useCallback(
    async (
      values: TwoFactorLoginValues,
      onSuccess?: (data: AuthBody) => void,
    ) => {
      try {
        const loginResponse = await api.auth.twoFactorLogin(values);
        if (isAuthBody(loginResponse.data)) {
          onLoginReq(loginResponse.data);
        }
        onSuccess?.(loginResponse.data);
      } catch (err) {
        console.error('Error:', err);
        popError(t('invalid-code'));
      }
    },
    [onLoginReq, popError, t],
  );

  const forgotPasswordRequest = useCallback(
    async (email: string, onSuccess?: () => void) => {
      try {
        await api.auth.forgotPasswordRequest(email);
        onSuccess?.();
      } catch (err) {
        console.error('Error:', err);
        popError(t('invalid-email'));
      }
    },
    [popError, t],
  );

  const resetPasswordRequest = useCallback(
    async (password: string, token: string, onSuccess?: () => void) => {
      try {
        await api.auth.resetPassword(password, token);
        onSuccess?.();
      } catch (err) {
        console.error('Error:', err);
        popError(t('auth-password-error'));
      }
    },
    [popError, t],
  );

  const logoutRequest = useCallback(() => {
    localStorage.clear();
    credentialsService.removeAuthBody();

    setIsLoggedIn(false);
  }, []);

  useEffect(() => {
    if (credentialsService.token || credentialsService.registrationToken) {
      (async () => {
        setIsLoading(true);
        try {
          const { data } = await (credentialsService.token
            ? api.user.getCurrentUser()
            : api.authProcess.getCurrentUser());
          setIsLoggedIn(!!data);
        } catch (e) {
          popError(t('session-expired'));
          console.error(e);
          logoutRequest();
        } finally {
          setIsLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      isLoggedIn,
      loginRequest,
      twoFactorLoginRequest,
      logoutRequest,
      onLoginReq,
      getToken,
      forgotPasswordRequest,
      resetPasswordRequest,
    }),
    [
      isLoggedIn,
      loginRequest,
      twoFactorLoginRequest,
      logoutRequest,
      onLoginReq,
      getToken,
      forgotPasswordRequest,
      resetPasswordRequest,
    ],
  );

  if (isLoading) return <FourDotLoader />;

  if (isLoggedIn)
    return (
      <AuthContext.Provider value={value as any}>
        <CurrentUserProvider>{children}</CurrentUserProvider>
      </AuthContext.Provider>
    );

  return (
    <AuthContext.Provider value={value as any}>{children}</AuthContext.Provider>
  );
};

export default Auth;
